import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  MotifDatePicker,
} from "@ey-xd/motif-react";
import { CalendarIcon } from "../../assets/Icons/Calendar";
import { ChevronDown } from "../../assets/Icons/ChevronDown";
import { CloseIcon } from "../../assets/Icons/CloseIcon";
import FilterFunnelIcon from "../../assets/Icons/FilterFunnelIcon";
import { InfoIcon } from "../../assets/Icons/InfoIcon";
import { SearchIcon } from "../../assets/Icons/SearchIcon";
import NavArrowUp from "../../assets/Icons/NavArrowUp";
import { NavDownArrow } from "../../assets/Icons/NavArrowDown";
import { Tooltip } from "../CommonUIComponents/Tooltip/Tooltip";
import { EditUnavailabilityModal } from "./EditUnavailabilityModal";
import { WithdrawUnavailabilityModal } from "./WithdrawUnavailabilityModal";
import "../UserManagement/UserManagement.scss";
import { UserManageemntService, LAAPService } from "../../api/services";
import { getNotificationRoleSegment } from "../../util/notificationRole";
import { formatDateShort } from "../../util/dateUtil";
import {
  hasDuplicateUnavailabilityRecord,
  isEligibleActiveTeamMember,
  isUnavailabilityRecordActionable,
  filterLaapRows,
  getLaapFilterOptions,
  LAAP_FILTER_KEYS,
  limitComment,
  MAX_COMMENT_LENGTH,
} from "./laap.utils";
import "./LAAP.scss";

// Derives a friendly display name from an email's local part (e.g. "john.doe" -> "John Doe").
const deriveNameFromEmail = (email) => {
  const localPart = String(email || "").split("@")[0] || "";

  return localPart
    .split(/[._]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

// Reduces the User Management listing to unique, active users keyed by email.
const getUniqueActiveUsers = (users) => {
  const seen = new Set();
  const uniqueUsers = [];

  (Array.isArray(users) ? users : []).forEach((user) => {
    if (!isEligibleActiveTeamMember(user)) {
      return;
    }

    const email = String(user.emailId || "").trim().toLowerCase();

    if (!email || seen.has(email)) {
      return;
    }

    seen.add(email);
    uniqueUsers.push({
      userId: user.userId ?? user.userGuId ?? "",
      email: user.emailId,
      name: deriveNameFromEmail(user.emailId),
    });
  });

  return uniqueUsers;
};

const TruncatedTableText = ({ value, className = "" }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const text = String(value ?? "");

  const checkTruncation = () => {
    const element = textRef.current;
    setIsTruncated(Boolean(element && element.scrollWidth > element.clientWidth));
  };

  useEffect(() => {
    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [text]);

  const trigger = (
    <span
      ref={textRef}
      className={`laap-cell-truncate ${className}`}
      tabIndex={isTruncated ? 0 : undefined}
    >
      {text}
    </span>
  );

  return isTruncated ? (
    <Tooltip trigger={trigger} tooltipText={text} placement="top" />
  ) : trigger;
};

const renderTableText = (value, className = "") => (
  <TruncatedTableText value={value} className={className} />
);

const createEmptyLaapFilters = () => ({
  name: [],
  dateRange: { start: null, end: null },
  businessDays: [],
  submittedBy: [],
  reason: [],
  status: [],
});

const LAAP_FILTER_LABELS = {
  name: "Name",
  businessDays: "Business Days",
  submittedBy: "Submitted By",
  reason: "Reason",
  status: "Status",
};

// Extracts the records array from the GetLAAPTrackerRecordsAsync response, whatever shape it comes in.
const getLaapRecordsList = (response) => {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.records)) {
    return payload.records;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.laapTrackerList)) {
    return payload.laapTrackerList;
  }

  if (Array.isArray(payload?.result)) {
    return payload.result;
  }

  return [];
};

const formatDateRangeDisplay = (startDate, endDate) => {
  const formattedStart = formatDateShort(startDate);
  const formattedEnd = formatDateShort(endDate);

  if (formattedStart && formattedEnd) {
    return `${formattedStart} - ${formattedEnd}`;
  }

  return formattedStart || formattedEnd || "";
};

// Formats a Date instance as YYYY-MM-DD for the create-record payload.
const formatDateForPayload = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Maps a raw LAAP tracker API record to the shape rendered by the tracker table.
const mapLaapRecord = (record, index) => {
  const startDate = record.startDate ?? record.fromDate ?? record.leaveStartDate;
  const endDate = record.endDate ?? record.toDate ?? record.leaveEndDate;
  const modifiedOn = record.modifiedOn ?? record.updatedOn ?? record.lastModifiedOn;
  const appliedOnDate = record.appliedOn ?? record.createdOn ?? record.submittedOn;

  return {
    id: record.recordId ?? record.id ?? record.laapTrackerId ?? index,
    userId: record.userId ?? record.employeeUserId ?? "",
    startDate,
    endDate,
    name: record.name ?? record.userName ?? record.employeeName ?? "",
    email: record.email ?? record.emailId ?? record.userEmail ?? "",
    dates: record.dateRange || formatDateRangeDisplay(startDate, endDate),
    modified: modifiedOn ? `Modified on ${formatDateShort(modifiedOn)}` : record.modified || "",
    businessDays: String(record.businessDays ?? record.noOfDays ?? record.totalDays ?? ""),
    submittedBy: record.submittedBy ?? record.createdBy ?? record.appliedBy ?? "",
    createdBy: record.createdBy ?? "",
    appliedOn: appliedOnDate ? `Applied on ${formatDateShort(appliedOnDate)}` : record.appliedOnLabel || "",
    reason: limitComment(record.reason ?? record.reasonName ?? ""),
    status: record.status ?? record.statusName ?? "Applied",
    halfDay: Boolean(record.halfDay ?? record.isHalfDay),
    withdrawn: Boolean(record.withdrawn ?? record.isWithdrawn),
  };
};

export const LAAP = () => {
  const [unavailabilityRows, setUnavailabilityRows] = useState([]);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [recordsError, setRecordsError] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [withdrawRecord, setWithdrawRecord] = useState(null);
  const [isEntryPanelExpanded, setIsEntryPanelExpanded] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [trackerSearchText, setTrackerSearchText] = useState("");
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(createEmptyLaapFilters);
  const [draftFilters, setDraftFilters] = useState(createEmptyLaapFilters);
  const [expandedFilters, setExpandedFilters] = useState({
    name: true,
    dateRange: true,
    businessDays: false,
    submittedBy: false,
    reason: false,
    status: true,
  });
  const [filterSearchTerms, setFilterSearchTerms] = useState({});
  const user = useSelector((state) => state.user?.user);
  const selectedRole = useSelector((state) => state.user?.selectedRole);
  const userRoles = useSelector((state) => state.user?.userRoles);
  const userGuid = useSelector((state) => state.user?.userGuid);
  const loggedInUserName = user?.name || user?.displayName || "User Name";
  const loggedInUserEmail = user?.email || user?.mail || user?.username || "";

  const currentRole =
    selectedRole ||
    (Array.isArray(userRoles) ? userRoles.find(Boolean) : null);
  const isRegionalLead =
    getNotificationRoleSegment(currentRole) === "RegionalLead";
  const isDidUser = getNotificationRoleSegment(currentRole) === "DID";
  const trackerColumnCount = isDidUser ? 6 : 7;
  console.log("Current user:", userGuid, "Current role:", currentRole);
  const loggedInUserId = currentRole?.userId || "";
  const loggedInRoleId = currentRole?.roleId || "";

  // Fetch the logged-in user's LAAP tracker records for the tracker table
  const fetchLaapRecords = useCallback(async () => {
    if (!loggedInUserId || !loggedInRoleId) {
      return;
    }

    try {
      setIsLoadingRecords(true);
      setRecordsError("");
      const response = await LAAPService.getRecords(
        loggedInUserId,
        loggedInRoleId,
      );

      setUnavailabilityRows(getLaapRecordsList(response).map(mapLaapRecord));
    } catch (error) {
      console.error("Error fetching LAAP tracker records:", error);
      setUnavailabilityRows([]);
      setRecordsError("Unable to load unavailability records.");
    } finally {
      setIsLoadingRecords(false);
    }
  }, [loggedInUserId, loggedInRoleId]);

  useEffect(() => {
    fetchLaapRecords();
  }, [fetchLaapRecords]);

  // Keep pagination in range whenever the record set or page size changes
  useEffect(() => {
    setCurrentPage(1);
  }, [unavailabilityRows.length, pageSize, trackerSearchText]);

  const filteredTrackerRows = useMemo(() => {
    const filterRows = filterLaapRows(
      unavailabilityRows,
      appliedFilters,
      !isDidUser,
    );
    const normalizedSearch = trackerSearchText.trim().toLowerCase();
    if (!normalizedSearch) {
      return filterRows;
    }

    return filterRows.filter((row) => {
      const status = row.withdrawn ? "withdrawn" : row.status;
      return [
        ...(isDidUser ? [] : [row.name, row.email]),
        row.dates,
        row.modified,
        row.businessDays,
        row.submittedBy,
        row.appliedOn,
        row.reason,
        status,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch));
    });
  }, [unavailabilityRows, trackerSearchText, isDidUser, appliedFilters]);

  const filterOptions = useMemo(() => {
    const keys = isDidUser ? LAAP_FILTER_KEYS.filter((key) => key !== "name") : LAAP_FILTER_KEYS;
    return Object.fromEntries(
      keys.map((key) => [
        key,
        getLaapFilterOptions(unavailabilityRows, draftFilters, key, !isDidUser),
      ]),
    );
  }, [unavailabilityRows, draftFilters, isDidUser]);

  const activeFilterCount = useMemo(
    () => Object.values(appliedFilters).reduce(
      (count, value) => count + (Array.isArray(value)
        ? value.length
        : Number(Boolean(value?.start || value?.end))),
      0,
    ),
    [appliedFilters],
  );
  const draftFilterCount = useMemo(
    () => Object.values(draftFilters).reduce(
      (count, value) => count + (Array.isArray(value)
        ? value.length
        : Number(Boolean(value?.start || value?.end))),
      0,
    ),
    [draftFilters],
  );

  const updateDraftFilter = (key, value) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
    setCurrentPage(1);
  };

  const openFilterPanel = () => {
    setDraftFilters({
      ...appliedFilters,
      dateRange: { ...appliedFilters.dateRange },
    });
    setIsFilterPanelOpen(true);
  };

  const clearDraftFilters = () => setDraftFilters(createEmptyLaapFilters());

  const applyDraftFilters = () => {
    setAppliedFilters({
      ...draftFilters,
      dateRange: { ...draftFilters.dateRange },
    });
    setCurrentPage(1);
    setIsFilterPanelOpen(false);
  };

  const cancelDraftFilters = () => {
    setDraftFilters({
      ...appliedFilters,
      dateRange: { ...appliedFilters.dateRange },
    });
    setIsFilterPanelOpen(false);
  };

  const getFilterDateValue = (value) => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    const text = String(value || "").trim();
    if (!text) return null;
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const handleFilterDateChange = (value) => {
    const [start, end] = Array.isArray(value) ? value : [value, null];
    updateDraftFilter("dateRange", {
      start: getFilterDateValue(start),
      end: getFilterDateValue(end),
    });
  };

  const toggleFilterValue = (key, value) => {
    const selected = draftFilters[key];
    updateDraftFilter(
      key,
      selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value],
    );
  };

  const toggleAllFilterValues = (key) => {
    const options = filterOptions[key] || [];
    const allSelected = options.length > 0 && options.every((item) =>
      draftFilters[key].includes(item),
    );
    updateDraftFilter(key, allSelected ? [] : [...options]);
  };

  const renderCheckboxFilter = (key) => {
    const options = filterOptions[key] || [];
    const searchTerm = String(filterSearchTerms[key] || "").trim().toLowerCase();
    const visibleOptions = searchTerm
      ? options.filter((option) => option.toLowerCase().includes(searchTerm))
      : options;
    const allSelected = options.length > 0 && options.every((option) =>
      draftFilters[key].includes(option),
    );

    return (
      <>
        <div className="filter-search-container">
          <SearchIcon width={16} height={16} color="#737387" />
          <input
            type="text"
            className="filter-search-input"
            placeholder="Search"
            value={filterSearchTerms[key] || ""}
            onChange={(event) => setFilterSearchTerms((previous) => ({
              ...previous,
              [key]: event.target.value,
            }))}
          />
        </div>
        <div className="filter-options">
          {options.length > 0 && (
            <label className="filter-option">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => toggleAllFilterValues(key)}
              />
              <span className="checkbox-custom" />
              <span className="filter-option-label">Select All</span>
            </label>
          )}
          {visibleOptions.map((option) => (
            <label key={option} className="filter-option">
              <input
                type="checkbox"
                checked={draftFilters[key].includes(option)}
                onChange={() => toggleFilterValue(key, option)}
              />
              <span className="checkbox-custom" />
              <span className="filter-option-label">{option}</span>
            </label>
          ))}
          {options.length === 0 && (
            <span className="filter-no-options">No options available</span>
          )}
        </div>
      </>
    );
  };

  const renderFilterAccordion = (key, label, content) => (
    <div className="filter-accordion" key={key}>
      <div
        className="filter-accordion-header"
        role="button"
        tabIndex={0}
        aria-expanded={Boolean(expandedFilters[key])}
        onClick={() => setExpandedFilters((previous) => ({
          ...previous,
          [key]: !previous[key],
        }))}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setExpandedFilters((previous) => ({
              ...previous,
              [key]: !previous[key],
            }));
          }
        }}
      >
        <span className="filter-accordion-title">{label}</span>
        <span className={`accordion-chevron${expandedFilters[key] ? " expanded" : ""}`}>
          <ChevronDown width={20} height={20} color="#dedee2" />
        </span>
      </div>
      {expandedFilters[key] && (
        <div className="filter-accordion-content">{content}</div>
      )}
    </div>
  );

  const totalPages = Math.max(1, Math.ceil(filteredTrackerRows.length / pageSize));
  const paginatedRows = useMemo(
    () =>
      filteredTrackerRows.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
      ),
    [filteredTrackerRows, currentPage, pageSize],
  );


  // Active Directory style team-member search state (Regional Lead only)
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [teamSearchText, setTeamSearchText] = useState("");
  const [showTeamSuggestions, setShowTeamSuggestions] = useState(false);
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(false);
  const teamSearchRef = useRef(null);

  const userName = isRegionalLead
    ? selectedTeamMember?.name || ""
    : loggedInUserName;
  const userEmail = isRegionalLead
    ? selectedTeamMember?.email || ""
    : loggedInUserEmail;

  const hasInvalidDateRange =
    startDate instanceof Date &&
    endDate instanceof Date &&
    startDate.getTime() > endDate.getTime();
  const hasValidDateRange =
    startDate instanceof Date &&
    endDate instanceof Date &&
    !hasInvalidDateRange;
  // Compare calendar day (not exact time) since pickers can set differing time-of-day components
  const isHalfDayEligible =
    hasValidDateRange &&
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate();

  // Fetch the User Management listing so a Regional Lead can search for a team member
  useEffect(() => {
    if (!isRegionalLead) {
      return;
    }

    let isMounted = true;

    const loadTeamMembers = async () => {
      try {
        setIsLoadingTeamMembers(true);
        const response = await UserManageemntService.fetchAllUsers();
        if (isMounted) {
          setTeamMembers(getUniqueActiveUsers(response?.data?.usersList));
        }
      } catch (error) {
        console.error("Error fetching user management users:", error);
        if (isMounted) {
          setTeamMembers([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingTeamMembers(false);
        }
      }
    };

    loadTeamMembers();

    return () => {
      isMounted = false;
    };
  }, [isRegionalLead]);

  // Close the suggestions dropdown when clicking outside of it
  useEffect(() => {
    if (!isRegionalLead) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (
        teamSearchRef.current &&
        !teamSearchRef.current.contains(event.target)
      ) {
        setShowTeamSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isRegionalLead]);

  const teamMemberSuggestions = useMemo(() => {
    const normalizedSearchText = teamSearchText.trim().toLowerCase();

    if (!normalizedSearchText) {
      return [];
    }

    return teamMembers.filter(
      (member) =>
        member.name.toLowerCase().includes(normalizedSearchText) ||
        member.email.toLowerCase().includes(normalizedSearchText),
    );
  }, [teamMembers, teamSearchText]);

  const handleTeamSearchChange = (event) => {
    const value = event.target.value;
    setTeamSearchText(value);
    setShowTeamSuggestions(value.trim() !== "");
    setSubmitRecordError("");
    if (selectedTeamMember) {
      setSelectedTeamMember(null);
    }
  };

  const handleSelectTeamMember = (member) => {
    setSelectedTeamMember(member);
    setTeamSearchText("");
    setShowTeamSuggestions(false);
    setSubmitRecordError("");
  };

  const handleClearTeamMember = () => {
    setSelectedTeamMember(null);
    setTeamSearchText("");
    setSubmitRecordError("");
  };

  const handleDateChange = (value) => {
    setSubmitRecordError("");
    if (!value) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    const [nextStartDate, nextEndDate] = Array.isArray(value)
      ? value
      : [value, null];
    setStartDate(nextStartDate instanceof Date ? nextStartDate : null);
    setEndDate(nextEndDate instanceof Date ? nextEndDate : null);
  };

  const [isHalfDay, setIsHalfDay] = useState(false);
  const [isManagerApproved, setIsManagerApproved] = useState(true);
  const [reasonText, setReasonText] = useState("");
  const [isSubmittingRecord, setIsSubmittingRecord] = useState(false);
  const [submitRecordError, setSubmitRecordError] = useState("");

  // Reset Half Day whenever the date range no longer qualifies (single-day range required)
  useEffect(() => {
    if (!isHalfDayEligible && isHalfDay) {
      setIsHalfDay(false);
    }
  }, [isHalfDayEligible, isHalfDay]);

  const handleSubmitRecord = async () => {
    if (
      !hasValidDateRange ||
      !isManagerApproved ||
      (isRegionalLead && !selectedTeamMember)
    ) {
      return;
    }

    const targetUserId = isRegionalLead
      ? selectedTeamMember?.userId || ""
      : loggedInUserId;

    if (
      hasDuplicateUnavailabilityRecord(unavailabilityRows, {
        userId: targetUserId,
        email: userEmail,
        startDate,
        endDate,
      })
    ) {
      setSubmitRecordError(
        "An unavailability record already exists for the selected person and dates.",
      );
      return;
    }

    const payload = {
      userId: targetUserId,
      startDate: formatDateForPayload(startDate),
      endDate: formatDateForPayload(endDate),
      isHalfDay,
      status: "Applied",
      reason: limitComment(reasonText).trim(),
      isWithdrawn: false,
      submitterRoleId:  loggedInRoleId,
    };

    // 

    try {
      setIsSubmittingRecord(true);
      setSubmitRecordError("");
      await LAAPService.saveRecord(payload);

      setStartDate(null);
      setEndDate(null);
      setIsHalfDay(false);
      setReasonText("");
      if (isRegionalLead) {
        handleClearTeamMember();
      }

      await fetchLaapRecords();
    } catch (error) {
      console.error("Error saving LAAP tracker record:", error);
      setSubmitRecordError(
        error?.message || "Unable to save unavailability record.",
      );
    } finally {
      setIsSubmittingRecord(false);
    }
  };

  const [isSavingRecord, setIsSavingRecord] = useState(false);
  const [saveRecordError, setSaveRecordError] = useState("");

  const handleSaveRecord = async (updatedRecord) => {
    const payload = {
      id: updatedRecord.id,
      userId: updatedRecord.userId || loggedInUserId,
      startDate: formatDateForPayload(updatedRecord.startDate),
      endDate: formatDateForPayload(updatedRecord.endDate),
      isHalfDay: Boolean(updatedRecord.halfDay),
      status: "Applied",
      reason: limitComment(updatedRecord.reason).trim(),
      isWithdrawn: false,
      submitterRoleId: loggedInRoleId,
    };

    try {
      setIsSavingRecord(true);
      setSaveRecordError("");
      await LAAPService.updateRecord(payload);
      setSelectedRecord(null);
      await fetchLaapRecords();
    } catch (error) {
      console.error("Error updating LAAP tracker record:", error);
      setSaveRecordError(
        error?.message || "Unable to update unavailability record.",
      );
    } finally {
      setIsSavingRecord(false);
    }
  };

  const [isWithdrawingRecord, setIsWithdrawingRecord] = useState(false);
  const [withdrawRecordError, setWithdrawRecordError] = useState("");

  const handleWithdrawRecord = async (record) => {
    try {
      setIsWithdrawingRecord(true);
      setWithdrawRecordError("");
      await LAAPService.deleteRecord(record.id);
      setWithdrawRecord(null);
      await fetchLaapRecords();
    } catch (error) {
      console.error("Error withdrawing LAAP tracker record:", error);
      setWithdrawRecordError(
        error?.message || "Unable to withdraw unavailability record.",
      );
    } finally {
      setIsWithdrawingRecord(false);
    }
  };

  return (
    <main className="laap-page" data-testid="laap-page">
      <section className="laap-entry-panel" aria-labelledby="laap-entry-title">
      <div className="laap-panel-heading">
        <h1 id="laap-entry-title">Manage Leave (LAAP)</h1>
        <button
          type="button"
          className="laap-icon-button"
          aria-label={isEntryPanelExpanded ? "Collapse log unavailability" : "Expand log unavailability"}
          aria-expanded={isEntryPanelExpanded}
          onClick={() => setIsEntryPanelExpanded((isExpanded) => !isExpanded)}
        >
          {isEntryPanelExpanded ? (
            <NavArrowUp width={16} height={16} color="#a4a3b1" />
          ) : (
            <NavDownArrow width={16} height={16} color="#a4a3b1" />
          )}
        </button>
      </div>

      {isEntryPanelExpanded && <div className="laap-entry-fields">
        {isRegionalLead ? (
          <div className="laap-user-details" ref={teamSearchRef}>
            <span className="laap-field-label">Name</span>
            {selectedTeamMember ? (
              <div className="laap-team-search-selected">
                <span className="laap-user-name">{selectedTeamMember.name}</span>
                <span className="laap-user-email">{selectedTeamMember.email}</span>
                <button
                  type="button"
                  className="laap-team-search-clear"
                  aria-label="Clear selected user"
                  onClick={handleClearTeamMember}
                >
                  <CloseIcon width={14} height={14} color="#A4A3B1" />
                </button>
              </div>
            ) : (
              <div className="laap-team-search-field">
                <SearchIcon width={16} height={16} color="#8f97bb" />
                <input
                  type="text"
                  className="laap-team-search-input"
                  placeholder="Search by name or email"
                  value={teamSearchText}
                  onChange={handleTeamSearchChange}
                  onFocus={() =>
                    setShowTeamSuggestions(teamSearchText.trim() !== "")
                  }
                  autoComplete="off"
                />
                {isLoadingTeamMembers && (
                  <span className="laap-team-search-loading">...</span>
                )}
                {showTeamSuggestions && teamMemberSuggestions.length > 0 && (
                  <ul className="laap-team-search-suggestions">
                    {teamMemberSuggestions.map((member) => (
                      <li
                        key={member.email}
                        className="laap-team-search-suggestion-item"
                        onClick={() => handleSelectTeamMember(member)}
                      >
                        <span className="laap-team-search-suggestion-name">
                          {member.name}
                        </span>
                        <span className="laap-team-search-suggestion-email">
                          {member.email}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {showTeamSuggestions &&
                  !isLoadingTeamMembers &&
                  teamSearchText.trim() !== "" &&
                  teamMemberSuggestions.length === 0 && (
                    <ul className="laap-team-search-suggestions">
                      <li className="laap-team-search-no-results">
                        No matching users found
                      </li>
                    </ul>
                  )}
              </div>
            )}
          </div>
        ) : (
          <div className="laap-user-details">
            <span className="laap-field-label">Name</span>
            <div className="laap-user-identity">
              <span className="laap-user-name">{userName}</span>
              {userEmail && <span className="laap-user-email">{userEmail}</span>}
            </div>
          </div>
        )}

        <div className="laap-date-halfday-group">
          <label className="laap-field laap-date-field">
            <span className="laap-field-label">
              Date range <span className="laap-required-marker">*</span>
            </span>
            <div
              className={`laap-datepicker-field${hasInvalidDateRange ? " laap-datepicker-field--error" : ""}`}
            >
              <MotifDatePicker
                range
                value={[startDate, endDate]}
                format="dd/MM/yyyy"
                locale="en-US"
                calendarPlacement="top-start"
                yearsAfter={5}
                yearsBefore={5}
                onChange={handleDateChange}
                dayPlaceholder="dd"
                monthPlaceholder="mm"
                yearPlaceholder="yyyy"
                clearIcon={
                  startDate || endDate ? (
                    <CloseIcon width={16} height={16} color="#A4A3B1" />
                  ) : null
                }
                calendarIcon={
                  <CalendarIcon width={16} height={16} color="#A4A3B1" />
                }
              />
            </div>
            {hasInvalidDateRange && (
              <span className="laap-validation-error" role="alert">
                Start date cannot be before End date
              </span>
            )}
          </label>

          <label className="laap-half-day-control">
            <input
              type="checkbox"
              checked={isHalfDay}
              disabled={!isHalfDayEligible}
              onChange={(event) => setIsHalfDay(event.target.checked)}
            />
            <span>Half Day</span>
          </label>
        </div>

        <label className="laap-field laap-reason-field">
          <span className="laap-field-label">Reason</span>
          <input
            type="text"
            placeholder="Write Comments Here"
            value={reasonText}
            maxLength={MAX_COMMENT_LENGTH}
            onChange={(event) => setReasonText(limitComment(event.target.value))}
          />
        </label>

        <label className="laap-approval-control">
          <input
            type="checkbox"
            checked={isManagerApproved}
            onChange={(event) => setIsManagerApproved(event.target.checked)}
          />
          <span>Approved by RL</span>
        </label>

        <button
          type="button"
          className="laap-submit-button"
          onClick={handleSubmitRecord}
          disabled={
            !hasValidDateRange ||
            !isManagerApproved ||
            (isRegionalLead && !selectedTeamMember) ||
            isSubmittingRecord
          }
        >
          {"Submit"}
        </button>
        {submitRecordError && (
          <span className="laap-validation-error" role="alert">
            {submitRecordError}
          </span>
        )}
      </div>}
      </section>

    <section className="laap-tracker" aria-labelledby="laap-tracker-title">
      <div className="laap-tracker-header">
        <h2 id="laap-tracker-title">Leave Tracker</h2>
        <div className="laap-tracker-tools">
          <button
            type="button"
            className={`filter-button${activeFilterCount ? " has-active-filters" : ""}`}
            aria-label="Filter leave tracker"
            aria-expanded={isFilterPanelOpen}
            onClick={openFilterPanel}
          >
            <FilterFunnelIcon width={20} height={20} color="#A4A3B1" />
          </button>
          <label className="laap-search-field">
            <SearchIcon width={18} height={18} color="#8f97bb" />
            <input
              type="search"
              placeholder="Search by Name, Date etc"
              value={trackerSearchText}
              onChange={(event) => setTrackerSearchText(event.target.value)}
            />
          </label>
        </div>
      </div>

      {isFilterPanelOpen && (
        <aside className="filter-panel laap-filter-panel" aria-label="Leave tracker filters">
          <div className="filter-panel-header">
            <h2 className="filter-panel-title">Filters</h2>
            <button type="button" className="filter-clear-button" onClick={clearDraftFilters}>
              Clear
            </button>
          </div>
          <p className="filter-panel-subtitle">Select filters below</p>
          <div className="filter-accordions">
            {!isDidUser && renderFilterAccordion(
              "name",
              "Name",
              renderCheckboxFilter("name"),
            )}
            {renderFilterAccordion(
              "dateRange",
              "Date range",
              <div className="laap-filter-date-picker laap-datepicker-field">
                <MotifDatePicker
                  range
                  value={[draftFilters.dateRange.start, draftFilters.dateRange.end]}
                  format="dd/MM/yyyy"
                  locale="en-US"
                  calendarPlacement="bottom-start"
                  yearsAfter={5}
                  yearsBefore={5}
                  onChange={handleFilterDateChange}
                  dayPlaceholder="dd"
                  monthPlaceholder="mm"
                  yearPlaceholder="yyyy"
                  clearIcon={
                    draftFilters.dateRange.start || draftFilters.dateRange.end ? (
                      <CloseIcon width={16} height={16} color="#A4A3B1" />
                    ) : null
                  }
                  calendarIcon={<CalendarIcon width={16} height={16} color="#A4A3B1" />}
                />
              </div>,
            )}
            {LAAP_FILTER_KEYS.filter((key) => key !== "name").map((key) =>
              renderFilterAccordion(
                key,
                LAAP_FILTER_LABELS[key],
                renderCheckboxFilter(key),
              ),
            )}
          </div>
          <div className="filter-panel-footer">
            <div className="filter-selected-info">
              <button
                type="button"
                className="clear-selection-button"
                aria-label="Clear filter selections"
                onClick={clearDraftFilters}
              >
                <CloseIcon width={14} height={14} color="#dedee2" />
              </button>
              <span>Selected {draftFilterCount} items</span>
            </div>
            <div className="filter-panel-actions">
              <button type="button" className="filter-cancel-button" onClick={cancelDraftFilters}>
                Cancel
              </button>
              <button type="button" className="filter-apply-button" onClick={applyDraftFilters}>
                Apply
              </button>
            </div>
          </div>
        </aside>
      )}

      <div className="laap-table-scroll">
        <table
          className={`laap-tracker-table${isDidUser ? " laap-tracker-table--did" : ""}`}
        >
          <thead>
            <tr>
              {!isDidUser && <th>Name</th>}
              <th>Leave Dates</th>
              <th>Business Days</th>
              <th>Submitted by</th>
              <th>Reason</th>
              <th>Status</th>
              <th>
                <span className="laap-action-header">
                  Action
                  <Tooltip
                    trigger={
                      <span
                        className="laap-action-info-trigger"
                        aria-label="Actions for leaves initiated by you"
                        tabIndex={0}
                      >
                        <InfoIcon width={16} height={16} color="#A4A3B1" />
                      </span>
                    }
                    tooltipText="Actions for leaves initiated by you"
                    placement="top"
                  />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoadingRecords && (
              <tr>
                <td colSpan={trackerColumnCount}>Loading unavailability records...</td>
              </tr>
            )}
            {!isLoadingRecords && recordsError && (
              <tr>
                <td colSpan={trackerColumnCount}>{recordsError}</td>
              </tr>
            )}
            {!isLoadingRecords &&
              !recordsError &&
              filteredTrackerRows.length === 0 && (
                <tr>
                  <td colSpan={trackerColumnCount}>
                    {trackerSearchText.trim() || activeFilterCount > 0
                      ? "No unavailability records match the selected filters or search."
                      : "No unavailability records found."}
                  </td>
                </tr>
              )}
            {!isLoadingRecords &&
              !recordsError &&
              paginatedRows.map((row) => (
              <tr key={row.id}>
                {!isDidUser && (
                  <td>
                    <div className="laap-cell-stack">
                      {renderTableText(row.name, "laap-cell-primary")}
                      {renderTableText(row.email, "laap-cell-secondary")}
                    </div>
                  </td>
                )}
                <td>
                  <div className="laap-cell-stack">
                    {renderTableText(row.dates, "laap-cell-primary")}
                    {renderTableText(row.modified, "laap-cell-secondary")}
                  </div>
                </td>
                <td>{row.businessDays}</td>
                <td>
                  <div className="laap-cell-stack">
                    {renderTableText(row.submittedBy, "laap-cell-primary")}
                    {renderTableText(row.appliedOn, "laap-cell-secondary")}
                  </div>
                </td>
                <td>{renderTableText(row.reason)}</td>
                <td><span className={`laap-status laap-status--${row.withdrawn ? "withdrawn" : row.status.toLowerCase()}`}>{row.withdrawn ? "Withdrawn" : row.status}</span></td>
                <td>
                  {!row.withdrawn &&
                    isUnavailabilityRecordActionable(row.endDate) &&
                    String(row.createdBy).trim().toLowerCase() ===
                      loggedInUserEmail.trim().toLowerCase() &&
                    loggedInUserEmail.trim() !== "" && (
                    <div className="laap-row-actions">
                      <button
                        type="button"
                        onClick={() => setSelectedRecord(row)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setWithdrawRecord(row)}
                      >
                        Withdraw
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <footer className="laap-pagination" aria-label="Table pagination">
        <label>
          Show
          <select
            aria-label="Rows per page"
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </label>
        <button
          type="button"
          aria-label="First page"
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        >
          «
        </button>
        <button
          type="button"
          aria-label="Previous page"
          onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
          disabled={currentPage === 1}
        >
          ‹
        </button>
        <span>
          {currentPage} / {totalPages}
        </span>
        <button
          type="button"
          aria-label="Next page"
          onClick={() =>
            setCurrentPage((page) => Math.min(totalPages, page + 1))
          }
          disabled={currentPage === totalPages}
        >
          ›
        </button>
        <button
          type="button"
          aria-label="Last page"
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        >
          »
        </button>
      </footer>
    </section>
      <EditUnavailabilityModal
        show={Boolean(selectedRecord)}
        record={selectedRecord}
        onClose={() => setSelectedRecord(null)}
        onSave={handleSaveRecord}
        isSaving={isSavingRecord}
        error={saveRecordError}
      />
      <WithdrawUnavailabilityModal
        show={Boolean(withdrawRecord)}
        record={withdrawRecord}
        onClose={() => setWithdrawRecord(null)}
        onConfirm={handleWithdrawRecord}
        isWithdrawing={isWithdrawingRecord}
        error={withdrawRecordError}
      />
    </main>
  );
};

export default LAAP;
